import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IContact } from '@osapp/model';
import { PlatformService } from '@osapp/services';
import { ITerminal } from 'apps/idl/src/modules/sce/models/iterminal';
import { SceService } from 'apps/idl/src/modules/sce/services/sce.service';
import { CapacitorPluginSce } from 'capacitor-plugin-sce';
import { from, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { EDeviceType } from '../../../enums/EDeviceType';
import { DrawerPopoverService } from '../../../services/drawer-popover.service';
import { LecteurStorageService } from '../../../services/lecteurStorage.service';
import { LoaderService } from '../../../services/loader.service';
import { PanneauService } from '../../../services/panneau.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmationSuppressionComponent } from '../../confirmation-suppression/confirmation-suppression.component';

@Component({
	selector: 'di-panneau-ajout-appareil',
	templateUrl: './panneau-ajout-appareil.component.html',
	styleUrls: ['./panneau-ajout-appareil.component.scss'],
})
export class PanneauAjoutAppareilComponent implements OnInit{

	@Input() terminal?: ITerminal;
	@Input() currentUser: IContact;
	public form: FormGroup;
	
	constructor(
		private fb: FormBuilder,
		private svcPanneau: PanneauService,
		private svcSce: SceService,
		private svcPlatform: PlatformService,
		private svcSnackbar: SnackbarService,
		private svcLoader: LoaderService,
		private svcDrawerPopover: DrawerPopoverService,
		private svcLecteurStorage: LecteurStorageService
	) { }

	ngOnInit(): void {
		this.form = this.fb.group({
			name: [this.terminal?.name ?? '', Validators.required],
		});
	}

	public handleSubmit(event: Event): void {
		event.preventDefault();
		if(!this.terminal){
			this.createLecteur();
		}else{
			this.modifyLecteur();
		}
	}

	private createLecteur(): void {
		if (!this.currentUser.finess) {
			this.svcSnackbar.showToast(
				"info",
				"bottom center",
				"Numéro de FINESS non défini. Veuillez le renseigner dans 'Mon Compte'",
				null,
				null,
				null,
				"7000"
			);
			this.svcPanneau.close();
		}
		if (this.form.valid) {
			this.svcLoader.showLoader("Ajout de l'appareil...");
			const deviceType: EDeviceType = this.svcPlatform.getDeviceType();
			this.svcSce.createLecteur(this.form.value["name"], deviceType).pipe(
				switchMap((terminal: ITerminal) => {
					if (deviceType === EDeviceType.ANDROID) {
						return from(Promise.all([
							this.svcLecteurStorage.setDeviceId(terminal.terminalId),
							CapacitorPluginSce.setDeviceId({ deviceId: terminal.terminalId })
						])).pipe(
							switchMap(() => this.svcSce.associerLecteurUF(terminal.terminalId, this.currentUser.finess))
						);
					}
					return this.svcSce.associerLecteurUF(terminal.terminalId, this.currentUser.finess);
				}),
				tap(() => {
					this.svcPanneau.close();
					this.svcLoader.hideLoader();
				})
			).subscribe();
		}
	}

	private modifyLecteur(): void {
		if (this.form.valid) {
			this.svcLoader.showLoader("Modification de l'appareil...");
			this.svcSce.modifyLecteur(this.form.value["name"], this.terminal.terminalId).pipe(
				tap(() => {
					this.svcPanneau.close();
					this.svcLoader.hideLoader();
				})
			).subscribe();
		}
	}

	public handleDelete(event: Event): void {
		event.preventDefault();
		if (!this.currentUser.finess) {
			this.svcSnackbar.showToast(
				"info",
				"bottom center",
				"Numéro de FINESS non défini. Veuillez le renseigner dans 'Mon Compte'",
				null,
				null,
				null,
				"7000"
			);
			this.svcPanneau.close();
		}

		this.svcDrawerPopover.open("Confirmer la suppression ?", "auto", event.currentTarget, ConfirmationSuppressionComponent, {
			onConfirm: () => {
				this.svcDrawerPopover.close();
				this.svcLoader.showLoader("Suppression de l'appareil...");
				this.svcSce.supprimerAssociationLecteurUF(this.terminal.terminalId, this.currentUser.finess).pipe(
					switchMap(() => this.svcSce.supprimerLecteur(this.terminal.terminalId)),
					switchMap(() => {
						if(!this.terminal.isCurrentDevice) return of(null);
						return from(this.svcLecteurStorage.getDeviceId()).pipe(
							switchMap((deviceId) => {
								if (deviceId) {
									return from(this.svcLecteurStorage.removeDeviceId()).pipe(
										catchError(() => of(null))
									);
								} else {
									return of(null);
								}
							})
						)
						
					}),
					tap(() => {
						this.svcPanneau.close();
						this.svcLoader.hideLoader();
					})
				).subscribe();
			},
			onCancel: () => {
				this.svcDrawerPopover.close();
			}
		})
	}

	public onCancel() {
		this.svcPanneau.close();
	}
}
